import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { Link, useParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, ConfirmationDialog, DataTable, StatusBadge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import PromoteVariantModal from './components/PromoteVariantModal';
import CreateResearchVariantModal from './components/CreateResearchVariantModal';
import {
    buildStudyLeaderboard,
    formatStudyStatus,
    formatTimestamp,
    getStudyObjectiveLabel,
    getStudyOutcomeLabel,
    getStudyOutcomeTone,
    getStudyStatusTone,
    isEntryOptimizerStudyActive,
    parsePositiveInt,
    parseStudyResults,
    summarizeNoQualifyingReason,
    type EntryOptimizerCandidate,
    type EntryOptimizerStudyVariantResult,
    type StudyLeaderboardRow
} from './entryOptimizerUtils';

/**
 * Study results: status, the variant leaderboard and the verdict. The leaderboard
 * leads with the metrics a trader actually reads — avg profit per trade (with a
 * "vs current" delta) and win rate — alongside the objective score, out-of-sample
 * edge and fold consistency. The score and profit can disagree (a higher-scoring
 * variation isn't always more profitable per trade), so the banner is careful not
 * to overclaim. Per-variant profit/win-rate aren't in the study summary, so they
 * are read from each finalist's best candidate. Promoting a variant changes the
 * entry's semantics wholesale, so the promote modal leads with the diff and attach
 * is gated on a passed confirmation.
 */
const EntryOptimizerStudyDetails = () => {
    const queryClient = useQueryClient();
    const { studyId: studyIdParam } = useParams();
    const studyId = parsePositiveInt(studyIdParam ?? null);

    const [researchTarget, setResearchTarget] = useState<EntryOptimizerStudyVariantResult | null>(null);
    const [promoteTarget, setPromoteTarget] = useState<EntryOptimizerStudyVariantResult | null>(null);
    const researchCandidateId = researchTarget?.bestResearchCandidateId ?? researchTarget?.bestCandidateId ?? null;
    const [confirmCancel, setConfirmCancel] = useState(false);

    const studyQuery = useAppQuery({
        ...trpc.entryOptimizer.study.queryOptions({ studyId: studyId ?? 0 }),
        enabled: studyId !== null,
        refetchInterval: (query) => {
            const study = query.state.data?.study;

            return study && isEntryOptimizerStudyActive(study) ? 15000 : false;
        }
    });

    const study = studyQuery.data?.study ?? null;
    const results = useMemo(() => (study ? parseStudyResults(study) : null), [study]);
    const leaderboard = useMemo(() => (study && results ? buildStudyLeaderboard(study, results) : null), [study, results]);

    const recommendedSummary = useMemo(() => {
        if (!results?.recommendedVariantKey) return null;

        return (
            results.variants.find((variant) => variant.variantKey === results.recommendedVariantKey)?.summary ??
            results.recommendedVariantKey
        );
    }, [results]);

    // Per-variant profit/win-rate (best candidate) and the reason a variant produced
    // no qualifying setting (its candidates' blocking reasons) both need the finalist
    // jobs' candidate lists, which aren't in the study summary.
    const finalistJobs = useMemo(() => (leaderboard?.finalists ?? []).filter((row) => row.jobId !== null), [leaderboard]);

    const candidateQueries = useQueries({
        queries: finalistJobs.map((row) => ({
            ...trpc.entryOptimizer.listCandidates.queryOptions({ jobId: row.jobId ?? 0 }),
            staleTime: 60_000
        }))
    });

    // Cheap to rebuild each render (a handful of candidates across the finalist jobs).
    const candidateMetrics = new Map<number, { avgProfit: number | null; winRate: number | null; entryCount: number }>();
    const candidatesByJob = new Map<number, EntryOptimizerCandidate[]>();

    for (const query of candidateQueries) {
        for (const candidate of query.data?.candidates ?? []) {
            candidateMetrics.set(candidate.id, {
                avgProfit: candidate.avgProfit !== null ? Number(candidate.avgProfit) : null,
                winRate: candidate.winRate !== null ? Number(candidate.winRate) : null,
                entryCount: candidate.entryCount
            });

            const jobCandidates = candidatesByJob.get(candidate.jobId) ?? [];

            jobCandidates.push(candidate);
            candidatesByJob.set(candidate.jobId, jobCandidates);
        }
    }

    const incumbentRow = leaderboard?.finalists.find((row) => row.isIncumbent) ?? null;

    const incumbentAvgProfit =
        incumbentRow?.bestCandidateId != null ? (candidateMetrics.get(incumbentRow.bestCandidateId)?.avgProfit ?? null) : null;

    const objectiveLabel = study ? getStudyObjectiveLabel(study.objective) : '';

    const cancelMutation = useMutation({
        ...trpc.entryOptimizer.cancelStudy.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: async () => {
            showToast('Study cancelled', 'success');
            await queryClient.invalidateQueries({ queryKey: trpc.entryOptimizer.study.queryKey({ studyId: studyId ?? 0 }) });
        }
    });

    const columns: ColumnDef<StudyLeaderboardRow>[] = [
        {
            accessorKey: 'summary',
            header: 'Variation',
            cell: ({ row }) => (
                <span className={row.original.isIncumbent ? 'font-medium text-zinc-100' : 'text-zinc-300'}>
                    {row.original.summary}
                    {row.original.isIncumbent ? ' (current)' : ''}
                </span>
            )
        },
        {
            id: 'outcome',
            header: 'Outcome',
            cell: ({ row }) => {
                const reason =
                    row.original.outcome === 'no_qualifying_setting' && row.original.jobId !== null
                        ? summarizeNoQualifyingReason(candidatesByJob.get(row.original.jobId))
                        : undefined;

                return (
                    <span title={reason}>
                        <StatusBadge status={row.original.outcome} tone={getStudyOutcomeTone(row.original.outcome)}>
                            {getStudyOutcomeLabel(row.original.outcome)}
                        </StatusBadge>
                    </span>
                );
            }
        },
        {
            id: 'avgProfit',
            header: 'Research profit / trade',
            cell: ({ row }) => {
                const candidateId = row.original.bestResearchCandidateId ?? row.original.bestCandidateId;
                const avgProfit = candidateId != null ? (candidateMetrics.get(candidateId)?.avgProfit ?? null) : null;

                if (avgProfit === null) return <span className="text-zinc-600">—</span>;

                const delta = !row.original.isIncumbent && incumbentAvgProfit !== null ? avgProfit - incumbentAvgProfit : null;

                return (
                    <div>
                        <div className="text-zinc-200">{avgProfit.toFixed(3)}%</div>
                        {delta !== null && (
                            <div className={delta >= 0 ? 'text-xs text-emerald-400' : 'text-xs text-rose-400'}>
                                {delta >= 0 ? '+' : ''}
                                {delta.toFixed(3)}% vs current
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            id: 'winRate',
            header: 'Research win rate',
            cell: ({ row }) => {
                const candidateId = row.original.bestResearchCandidateId ?? row.original.bestCandidateId;
                const winRate = candidateId != null ? (candidateMetrics.get(candidateId)?.winRate ?? null) : null;

                if (winRate === null) return <span className="text-zinc-600">—</span>;

                return <span className="text-zinc-200">{(winRate * 100).toFixed(1)}%</span>;
            }
        },
        {
            accessorKey: 'adjustedScore',
            header: 'Overall score',
            cell: ({ row }) =>
                row.original.adjustedScore !== null ? (
                    <span
                        className={row.original.outcome === 'recommended' ? 'font-medium text-emerald-300' : 'text-zinc-200'}
                        title={`Ranks variations on ${objectiveLabel}, after the multi-variation penalty`}
                    >
                        {row.original.adjustedScore.toFixed(3)}
                    </span>
                ) : (
                    <span className="text-zinc-600">—</span>
                )
        },
        {
            accessorKey: 'bestHoldoutLift',
            header: 'Holds up on unseen data',
            cell: ({ row }) => {
                const lift = row.original.bestHoldoutLift;

                if (lift === null) return <span className="text-zinc-600">—</span>;

                return (
                    <span
                        className={lift > 0 ? 'text-emerald-300' : 'text-rose-300'}
                        title="Edge on the held-out period the search never tuned against"
                    >
                        {lift > 0 ? '+' : ''}
                        {lift.toFixed(3)}
                    </span>
                );
            }
        },
        {
            accessorKey: 'bestCvSignAgreement',
            header: 'Consistency',
            cell: ({ row }) => {
                const agreement = row.original.bestCvSignAgreement;

                if (agreement === null) return <span className="text-zinc-600">—</span>;

                const tone = agreement >= 0.8 ? 'text-emerald-300' : agreement >= 0.6 ? 'text-amber-300' : 'text-rose-300';

                return (
                    <span className={tone} title="Share of validation folds where the edge kept its direction">
                        {Math.round(agreement * 100)}%
                    </span>
                );
            }
        },
        {
            accessorKey: 'entryCount',
            header: 'Research trades',
            cell: ({ row }) => {
                // Prefer the best setting's qualifying-trade count, so it matches the avg
                // profit / win rate above; fall back to the variant's total entries.
                const candidateId = row.original.bestResearchCandidateId ?? row.original.bestCandidateId;
                const candidateTrades = candidateId != null ? (candidateMetrics.get(candidateId)?.entryCount ?? null) : null;

                const trades = candidateTrades ?? row.original.entryCount;

                return trades !== null ? trades.toLocaleString() : '—';
            }
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                    {row.original.jobId !== null && (
                        <Link className="text-cyan-400 hover:underline" to={`/user/entryOptimizer/${row.original.jobId}`}>
                            View
                        </Link>
                    )}
                    {!row.original.isIncumbent &&
                        row.original.stage === 'full' &&
                        row.original.jobId !== null &&
                        (row.original.bestResearchCandidateId ?? row.original.bestCandidateId) != null && (
                            <Button
                                disabled={study?.status !== 'completed'}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => setResearchTarget(row.original)}
                            >
                                Create variant…
                            </Button>
                        )}
                </div>
            )
        }
    ];

    if (studyId === null) {
        return (
            <UserLayout containerSize="1100px" title="Variant Study - Project 314">
                <Callout variant="error">Invalid study id.</Callout>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1100px" title={`Variation Search #${studyId} - Project 314`}>
            <div className="mb-6 flex items-start justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-zinc-100">Variation Search #{studyId}</h1>
                    {study && (
                        <p className="mt-1 text-sm text-zinc-400">
                            Strategy #{study.strategyId} · {study.duration} timeframe · {formatTimestamp(study.createdAt)}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {study && (
                        <StatusBadge status={study.status} tone={getStudyStatusTone(study.status)}>
                            {formatStudyStatus(study.status)}
                        </StatusBadge>
                    )}
                    {study && isEntryOptimizerStudyActive(study) && (
                        <Button size="sm" variant="outline-dark" onClick={() => setConfirmCancel(true)}>
                            Cancel
                        </Button>
                    )}
                </div>
            </div>

            {studyQuery.isLoading ? (
                <DataLoadingSpinner />
            ) : studyQuery.error instanceof Error ? (
                <Callout variant="error">{studyQuery.error.message}</Callout>
            ) : study ? (
                <>
                    {study.errorMessage && <Callout variant="error">{study.errorMessage}</Callout>}

                    {study.status === 'completed' && results && (
                        <Callout className="mb-4" variant={results.verdict === 'keep_incumbent' ? 'info' : 'success'}>
                            {results.verdict === 'keep_incumbent'
                                ? 'Keep your current entry — no variation beat it once the extra testing was accounted for' +
                                  `${results.stageCompleted === 'pilot' ? ' (decided early: no variation produced a validated improvement)' : ''}.`
                                : `Top-scoring variation: ${recommendedSummary} (★). It ranked highest on ${objectiveLabel} and held up ` +
                                  'best on unseen data — but compare avg profit/trade and win rate below, since the highest-scoring ' +
                                  'variation isn’t always the most profitable per trade. Create an inactive family variant after real-entry confirmation.'}
                        </Callout>
                    )}

                    {isEntryOptimizerStudyActive(study) && (
                        <Callout className="mb-4" variant="info">
                            {formatStudyStatus(study.status)} — each variation is a full test run, so this takes a while. Results fill in as
                            they finish; you don&apos;t need to keep this page open.
                        </Callout>
                    )}

                    <Callout className="mb-4" variant="info">
                        Create an inactive family variant to test an entry treatment. For a completed full-universe job with weak economic
                        results, open its sweep with View to choose a candidate for research.
                    </Callout>
                    <Card
                        subtitle={
                            leaderboard
                                ? results?.stageCompleted === 'pilot'
                                    ? `${leaderboard.totalVariants} variations tested in the pilot round · no full-universe round was run.`
                                    : `${leaderboard.totalVariants} variations tested · ${leaderboard.fullCount} reached full-universe testing · ` +
                                      `${leaderboard.screenedOutCount} screened out in the pilot round. Best score first; ★ is the recommendation.`
                                : 'Every variation compared head-to-head. Best first; ★ is the recommendation.'
                        }
                        title="Variations"
                    >
                        {leaderboard ? (
                            <>
                                <DataTable columns={columns} data={leaderboard.finalists} />

                                {leaderboard.screenedOut.length > 0 && (
                                    <details className="mt-4">
                                        <summary className="cursor-pointer text-sm text-zinc-400 hover:text-zinc-200">
                                            {leaderboard.screenedOutCount} variations screened out in the pilot round
                                        </summary>
                                        <p className="mt-2 text-xs text-zinc-500">
                                            These ran on a small pilot sample but didn&apos;t rank high enough to advance to full
                                            validation, so they were never run across the whole universe.
                                        </p>
                                        <ul className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-400 sm:grid-cols-3">
                                            {leaderboard.screenedOut.map((variant) => (
                                                <li key={variant.variantKey}>{variant.summary}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}

                                {leaderboard.notRunnable.length > 0 && (
                                    <details className="mt-3">
                                        <summary className="cursor-pointer text-sm text-zinc-400 hover:text-zinc-200">
                                            {leaderboard.notRunnable.length} variations couldn&apos;t be tested
                                        </summary>
                                        <ul className="mt-2 space-y-1 text-sm text-zinc-500">
                                            {leaderboard.notRunnable.map((variant) => (
                                                <li key={variant.variantKey}>
                                                    {variant.summary}
                                                    {variant.notRunnableReasons.length > 0
                                                        ? ` — ${variant.notRunnableReasons.join('; ')}`
                                                        : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </>
                        ) : (
                            <p className="text-sm text-zinc-500">Results appear when the search completes.</p>
                        )}
                    </Card>
                </>
            ) : null}

            {researchTarget?.jobId != null && researchTarget.stage === 'full' && researchCandidateId !== null && (
                <CreateResearchVariantModal
                    key={`${researchTarget.jobId}:${researchCandidateId}`}
                    jobId={researchTarget.jobId}
                    candidateId={researchCandidateId}
                    summary={`${researchTarget.summary}${researchTarget.researchCandidatePassesGates === false ? ' Economic checks did not pass; this is an exploratory research trial.' : ''}`}
                    onClose={() => setResearchTarget(null)}
                    onAdvanced={() => {
                        setPromoteTarget({ ...researchTarget, bestCandidateId: researchCandidateId });
                        setResearchTarget(null);
                    }}
                />
            )}

            <PromoteVariantModal
                key={promoteTarget?.variantKey ?? 'closed'}
                study={study}
                variant={promoteTarget}
                onClose={() => setPromoteTarget(null)}
            />

            <ConfirmationDialog
                confirmText="Cancel search"
                isOpen={confirmCancel}
                message="Cancel this variation search? Runs already in progress finish, but no new ones start."
                title="Cancel variation search"
                onCancel={() => setConfirmCancel(false)}
                onConfirm={() => {
                    setConfirmCancel(false);
                    cancelMutation.mutate({ studyId });
                }}
            />
        </UserLayout>
    );
};

export default EntryOptimizerStudyDetails;
